import '@css/dropzone.scss';
// Side-effect import: Dropzone 5 is CommonJS and puts itself on window.
import '~dropzone/dist/dropzone.js';

// Dropzone 5 ships no types, so this covers only what is used here.
interface DropzoneFile {
    type: string;
    previewElement: HTMLElement | null;
    xhr?: { response: string };
}

interface DropzoneInstance {
    on: (event: string, handler: (file: DropzoneFile) => void) => void;
    getAcceptedFiles: () => DropzoneFile[];
}

interface DropzoneConstructor {
    new (selector: string, options: Record<string, unknown>): DropzoneInstance;
    autoDiscover: boolean;
}

interface FreeformReadyEvent extends Event {
    options: { ajax: boolean; errorBannerMessage: string };
}

interface FreeformAjaxSuccessEvent extends Event {
    response: { returnUrl: string };
}

interface FreeformSubmitEvent extends Event {
    isBackButtonPressed?: boolean;
}

declare global {
    interface Window {
        Dropzone: DropzoneConstructor;
    }
}

/**
 * Upload field plus Freeform wiring for the estimate form.
 *
 * This used to be an inline script in offerte_aanvraag/show.twig with the
 * translated strings interpolated by Twig, which meant it could never be covered
 * by a CSP hash. The strings now ride along as data attributes on the preview
 * template element, which this code already needed to read anyway.
 */
const initDropzone = () => {
    const template = document.getElementById('dropzone-template-preview');
    if (!template || !document.querySelector('.dropzone-container')) return;

    const text = {
        pdfAlternative: template.dataset.pdfAlternative ?? '',
        cancelUpload: template.dataset.cancelUpload ?? '',
        removeFile: template.dataset.removeFile ?? '',
        formError: template.dataset.formError ?? '',
    };

    window.Dropzone.autoDiscover = false;

    const dropzone = new window.Dropzone('.dropzone-container', {
        paramName: 'dropzone-file',
        url: '/upload',
        maxFilesize: 50,
        addRemoveLinks: true,
        dictCancelUpload: text.cancelUpload,
        dictRemoveFile: text.removeFile,
        previewTemplate: template.innerHTML,
    });

    dropzone.on('complete', (file) => {
        if (!file.previewElement) return;

        if (file.type === 'application/pdf') {
            const notification = file.previewElement.querySelector('.dz-custom-notifications');
            if (notification) {
                notification.innerHTML = `<div class="alert alert-warning">${text.pdfAlternative}</div>`;
            }
        }

        const progress = file.previewElement.querySelector<HTMLElement>('.dz-progress');
        if (progress) progress.style.display = 'none';
    });

    dropzone.on('error', (file) => {
        const message = file.previewElement?.querySelector('[data-dz-errormessage]');
        message?.parentElement?.classList.add('alert', 'alert-danger');
    });

    const form = document.querySelector<HTMLFormElement>('form.freeform-form');
    if (!form) return;

    form.addEventListener('freeform-ready', (event) => {
        const ready = event as FreeformReadyEvent;
        ready.options.ajax = true;
        ready.options.errorBannerMessage = text.formError;
    });

    form.addEventListener('freeform-ajax-success', (event) => {
        window.location.href = (event as FreeformAjaxSuccessEvent).response.returnUrl;
    });

    form.addEventListener('freeform-ajax-error', () => {
        form.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });

    // Hand the uploaded files to Freeform: the upload happens outside the form,
    // so the submit has to carry the server's responses in a hidden field.
    form.addEventListener('freeform-on-submit', (event) => {
        if ((event as FreeformSubmitEvent).isBackButtonPressed) return;

        const uploaded = dropzone
            .getAcceptedFiles()
            .map((file) => file.xhr?.response)
            .filter((response): response is string => {
                if (!response) return false;
                try {
                    return JSON.parse(response).fileName !== '';
                } catch {
                    return false;
                }
            });

        const input = document.querySelector<HTMLInputElement>('#files-dropzone');
        if (input) input.value = JSON.stringify(uploaded);
    });
};

document.addEventListener('DOMContentLoaded', initDropzone);
